import { useState, useEffect } from "react";
import axios from "axios";
import useLenis from "../hooks/useLenis";
import { useContent } from "../context/ContentContext";
import { Navbar } from "../components/Navbar";
import { Hero } from "../components/Hero";
import { Marquee } from "../components/Marquee";
import { WhatsInside } from "../components/WhatsInside";
import { Manifesto } from "../components/Manifesto";
import { SalesCounter } from "../components/SalesCounter";
import { ProductBuy } from "../components/ProductBuy";
import { Comparison } from "../components/Comparison";
import { DirectoryUpsell } from "../components/DirectoryUpsell";
import { Consulting } from "../components/Consulting";
import { Testimonials } from "../components/Testimonials";
import { EmailCapture } from "../components/EmailCapture";
import { FAQ } from "../components/FAQ";
import { Footer } from "../components/Footer";
import { TawkChat } from "../components/TawkChat";
import { CheckoutModal } from "../components/CheckoutModal";
import { DiscountBanner } from "../components/DiscountBanner";
import { ExitOffer } from "../components/ExitOffer";
import { trackAB } from "../lib/ab";
import { Seo, SITE } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtPrice = (amount, currency) => {
  if (amount == null) return null;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase(), minimumFractionDigits: 0 }).format(amount / 100);
};

const DIRECTORY_KEY = "gtg_optout_directory";

export default function Landing() {
  useLenis();
  const { content } = useContent();
  const [price, setPrice] = useState(null);
  const [prices, setPrices] = useState({});
  const [checkout, setCheckout] = useState(null); // { key, title, price }

  useEffect(() => {
    trackAB("impression");
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("gtg_ref", ref);
    axios.get(`${API}/product`).then((res) => setPrice(fmtPrice(res.data.amount, res.data.currency))).catch(() => {});
    axios.get(`${API}/products`).then((res) => {
      const map = {};
      (res.data || []).forEach((p) => { map[p.lookup_key] = fmtPrice(p.amount, p.currency); });
      setPrices(map);
    }).catch(() => {});
  }, []);

  const nameFor = (key) => {
    if (key === "gtg_pack_onetime") return content.product?.name || "Privacy & Disappearance Pack";
    if (key === DIRECTORY_KEY) return content.addon?.name || "Data Broker Opt-Out Directory";
    const tier = (content.consulting?.tiers || []).find((t) => t.lookup_key === key);
    return tier ? tier.name : "Ghost The Grid";
  };

  const buy = (lookupKey = "gtg_pack_onetime") => {
    setCheckout({ key: lookupKey, title: nameFor(lookupKey), price: prices[lookupKey] || (lookupKey === "gtg_pack_onetime" ? price : null) });
  };

  const faqs = content?.faqs || [];
  const testimonials = content?.testimonials || [];
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "Privacy & Disappearance Pack",
      description: "A 12-node privacy playbook: remove yourself from 30+ data brokers, stop location and ad tracking, lock down accounts and devices, plus a bonus encrypted-email chapter. One-time payment, lifetime access, no subscription.",
      brand: { "@type": "Organization", name: "Ghost The Grid" },
      offers: { "@type": "Offer", price: "29.00", priceCurrency: "USD", availability: "https://schema.org/InStock", url: `${SITE}/#buy` },
      ...(testimonials.length
        ? {
            review: testimonials.map((t) => ({
              "@type": "Review",
              author: { "@type": "Person", name: t.handle || "Verified buyer" },
              reviewBody: t.quote,
            })),
          }
        : {}),
    },
    faqs.length && {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ].filter(Boolean);

  return (
    <div className="App noise-overlay relative">
      <Seo
        title="Ghost The Grid — Erase Your Digital Footprint & Disappear From Data Brokers"
        description="A one-time, no-subscription playbook to remove yourself from data brokers, stop tracking, and vanish from the grid. 12 guided nodes, 30+ opt-outs, for $29."
        path="/"
        jsonLd={jsonLd}
      />
      <TawkChat />
      <DiscountBanner onCta={() => buy()} />
      <Navbar onBuy={() => buy()} />
      <Hero onBuy={() => buy()} price={price} />
      <Marquee />
      <WhatsInside />
      <Manifesto />
      <SalesCounter />
      <ProductBuy onBuy={() => buy()} loading={false} price={price} />
      <Comparison />
      <DirectoryUpsell onBuy={() => buy(DIRECTORY_KEY)} loading={false} price={prices[DIRECTORY_KEY]} />
      <Consulting prices={prices} onBuy={buy} buyingKey={null} />
      <Testimonials />
      <EmailCapture />
      <FAQ />
      <Footer onBuy={() => buy()} />
      <CheckoutModal
        open={!!checkout}
        lookupKey={checkout?.key}
        title={checkout?.title}
        price={checkout?.price}
        onClose={() => setCheckout(null)}
      />
      <ExitOffer onClaim={() => buy()} />
    </div>
  );
}
